import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocations } from '@/stores/locations';
import { useRole } from '@/stores/identity';
import { LocationStatusBadge } from '@/components/LocationStatusBadge';
import { LocationCreateForm } from '@/components/LocationCreateForm';

/**
 * Locations index. Shows all locations the caller can see (RLS-scoped).
 * Owners get a "+ New location" button that opens an inline form.
 */
export default function LocationsList() {
  const status = useLocations((s) => s.status);
  const items = useLocations((s) => s.items);
  const error = useLocations((s) => s.error);
  const load = useLocations((s) => s.load);
  const role = useRole();
  const isOwner = role === 'owner';

  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (status === 'idle') void load();
  }, [status, load]);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Locations</h1>
            <p className="text-sm text-muted-foreground">
              Pharmacies under your organization. Each has its own hours,
              staff, and schedule.
            </p>
          </div>
          {isOwner && !showCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              + New location
            </button>
          )}
        </div>

        {showCreate && isOwner && (
          <div className="rounded-lg border border-border bg-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-medium">Add a location</h2>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="text-sm text-muted-foreground hover:underline"
              >
                Cancel
              </button>
            </div>
            <LocationCreateForm
              onCreated={() => setShowCreate(false)}
              submitLabel="Add location"
            />
          </div>
        )}

        {status === 'loading' && (
          <p className="text-sm text-muted-foreground">Loading locations...</p>
        )}
        {status === 'error' && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}
        {status === 'loaded' && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No locations yet. {isOwner && 'Click "+ New location" to add the first.'}
          </div>
        )}

        {status === 'loaded' && items.length > 0 && (
          <ul className="space-y-2">
            {items.map((loc) => (
              <li
                key={loc.id}
                className="rounded-lg border border-border bg-card p-4 hover:bg-muted/30"
              >
                <Link
                  to={`/locations/${loc.id}`}
                  className="flex items-center justify-between gap-4"
                >
                  <div className="space-y-0.5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-medium truncate">
                        {loc.name}
                      </h3>
                      <LocationStatusBadge status={loc.status} />
                    </div>
                    {loc.address && (
                      <p className="text-xs text-muted-foreground truncate">
                        {loc.address}
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">
                    {loc.timezone ?? 'org default'} ·{' '}
                    {loc.shift_split_time.slice(0, 5)}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
